import { useEffect, useMemo } from "react"

import type { MetricIdentityThresholdSource } from "@/metrics/catalog"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"

import type { MetricWidgetConfig } from "../components/BaseWidget"
import type { GroupByType } from "../types/chart-types"
import type { DashboardWidgetConfig } from "../types/core"
import type { MetricValue } from "../utils/chartShared"
import { useTableWidgetData as useModernTableWidgetData } from "./useTableWidgetDataTransport"

type TableWidgetConfig = MetricWidgetConfig &
  DashboardWidgetConfig & {
    type: "table"
    groupBy?: GroupByType
  }

type MetricRecordMap = Record<string, MetricIdentityThresholdSource>

interface MetricTypeInfo {
  outputType: "string" | "float"
  valueField: "metric_output_string_value" | "metric_output_float_value"
}

interface UseTableWidgetDataOptions {
  config: TableWidgetConfig
}

interface UseTableWidgetDataResult {
  error: string | null
  hasMetrics: boolean
  isGlobalOverrideActive: boolean
  isLoading: boolean
  metricRecords?: MetricRecordMap
  metricTypeInfo?: Record<string, MetricTypeInfo>
  metricValues: MetricValue[]
  selectedMetric: MetricIdentityThresholdSource | null
  dateRange: { start: Date; end: Date }
}

function resolveMetricIds(config: TableWidgetConfig) {
  if (config.filters?.metricIds?.length) {
    return config.filters.metricIds
  }

  if (config.metricIds?.length) {
    return config.metricIds
  }

  if (config.metricId) {
    return [config.metricId]
  }

  return []
}

function buildMetricTypeInfo(
  metricRecords: MetricRecordMap | null,
  metricIds: string[],
): Record<string, MetricTypeInfo> {
  if (!metricRecords || metricIds.length === 0) {
    return {}
  }

  return metricIds.reduce<Record<string, MetricTypeInfo>>((acc, metricId) => {
    const metricRecord = metricRecords[metricId]
    if (!metricRecord) {
      return acc
    }

    const isBinaryLLM = metricRecord.metric_manager === "manager_llm_binary"

    acc[metricId] = {
      outputType: isBinaryLLM ? "string" : "float",
      valueField: isBinaryLLM
        ? "metric_output_string_value"
        : "metric_output_float_value",
    }

    return acc
  }, {})
}

export function useTableWidgetData({
  config,
}: UseTableWidgetDataOptions): UseTableWidgetDataResult {
  const { trackEvent } = useAnalytics()
  const result = useModernTableWidgetData({ config })

  const effectiveMetricIds = useMemo(() => resolveMetricIds(config), [config])
  const hasMetricId = Boolean(config.metricId)

  const selectedMetric = useMemo(() => {
    if (!result.metricRecords) {
      return null
    }

    if (config.metricId) {
      return result.metricRecords[config.metricId] ?? null
    }

    return effectiveMetricIds.length > 0
      ? (result.metricRecords[effectiveMetricIds[0]] ?? null)
      : null
  }, [config.metricId, effectiveMetricIds, result.metricRecords])

  const metricTypeInfo = useMemo(
    () => buildMetricTypeInfo(result.metricRecords ?? null, effectiveMetricIds),
    [effectiveMetricIds, result.metricRecords],
  )

  useEffect(() => {
    if (hasMetricId && !result.isLoading && result.metricValues.length > 0) {
      trackEvent(TRACKING_EVENTS.WIDGET_METRIC_LOADED, {
        data_points: result.metricValues.length,
        is_monitoring: config.dataSource === "uploaded",
        metric_id: config.metricId,
      })
    }
  }, [
    config.dataSource,
    config.metricId,
    hasMetricId,
    trackEvent,
    result.isLoading,
    result.metricValues.length,
  ])

  useEffect(() => {
    if (hasMetricId && result.error) {
      trackEvent(TRACKING_EVENTS.WIDGET_METRIC_ERROR, {
        error: result.error,
        metric_id: config.metricId,
      })
    }
  }, [config.metricId, hasMetricId, trackEvent, result.error])

  return {
    dateRange: result.dateRange,
    error: result.error,
    hasMetrics: result.hasMetrics,
    isGlobalOverrideActive: result.isGlobalOverrideActive,
    isLoading: result.isLoading,
    metricRecords: result.metricRecords,
    metricTypeInfo:
      Object.keys(metricTypeInfo).length > 0 ? metricTypeInfo : undefined,
    metricValues: result.metricValues,
    selectedMetric,
  }
}
